// Grouping layer on top of useDataTable.
//
// The base hook handles search / sort / pagination over a flat array. This
// wrapper bins the SAME current page of rows into groups using a caller-
// supplied grouper, tracks collapse state per group, and persists which
// groups are collapsed under a stable localStorage key. The hook stays
// purely client-side and adds zero behaviour when grouping is disabled
// (groupBy === 'none').
//
// Pagination semantics: paging happens on the underlying rows (same as
// before). Groups are computed from the already-paginated view, so a
// "page" of 25 rows can show e.g. 3 groups summing 25 items. Collapsed
// groups still consume rows on the page — their children just don't
// render. That matches the spec's "hidden rows still count toward the
// page total".

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { EntityGroupSummary } from './api'
import type { UseDataTableResult } from './data-table'

export interface GroupOption {
  /** Stable key written to URL/localStorage. 'none' disables grouping. */
  key: string
  /** German label shown in the dropdown. */
  label: string
}

export interface Group<T> {
  /** Unique key inside this view. Used as localStorage entry + react key. */
  key: string
  /** Display label rendered in the sticky group header. */
  label: string
  /** All rows belonging to this group on the current page. */
  items: T[]
  /** True when the header was clicked closed (rows should be hidden). */
  collapsed: boolean
  /** How many of the items are flagged `restricted: true`. Optional badge. */
  restrictedCount: number
  /** Optional color (tailwind color name) for manual group swatch. */
  color?: string | null
  /** True for the synthetic "Nicht gruppiert" bucket — render muted. */
  muted?: boolean
}

export interface UseGroupedTableOptions<T> {
  /** Page identifier — combined with `groupBy` to form the persistence key. */
  pageKey: string
  /** Available "group by" choices (incl. 'none' = off). Stable reference. */
  options: GroupOption[]
  /** Default selection. Must be present in `options`. */
  defaultGroupBy?: string
  /** Compute group key + label per row. Called once per row when grouping. */
  groupBy: (row: T, groupKey: string) => { key: string; label: string } | null
  /** Optional manual groups (Stammdaten-Ordner). When provided, the hook
   *  emits a synthetic `manual` mode + uses `getId` to bucket rows by id. */
  manualGroups?: EntityGroupSummary[]
  /** Extract the row id for manual-group membership lookup. Required when
   *  `manualGroups` or `manualGroupsEnabled` is set. */
  getId?: (row: T) => string
  /** Force the "Manuelle Gruppen" option to appear even when no groups
   *  exist yet (admin can then create the first one). */
  manualGroupsEnabled?: boolean
}

export interface UseGroupedTableResult<T> {
  groupBy: string
  setGroupBy: (key: string) => void
  options: GroupOption[]
  /** True when groupBy === 'none' — UI should render the plain table. */
  disabled: boolean
  /** Already-paginated, grouped view. Empty when disabled. */
  groups: Group<T>[]
  /** Flat list of currently-VISIBLE rows (collapsed groups contribute zero). */
  visibleRows: T[]
  /** Set of every group key on the current page (for select-all toggling). */
  allGroupKeys: string[]
  toggleGroup: (key: string) => void
  expandAll: () => void
  collapseAll: () => void
  /** True if every group on the current page is collapsed. */
  allCollapsed: boolean
}

/** Wrap a `useDataTable` result with collapsible group rendering. The base
 *  hook keeps full control over filter/sort/pagination — we only consume
 *  its `rows` (the current page) and re-shape them. */
export function useGroupedTable<T extends { restricted?: boolean }>(
  table: UseDataTableResult<T>,
  options: UseGroupedTableOptions<T>,
): UseGroupedTableResult<T> {
  const {
    pageKey,
    options: groupOptions,
    defaultGroupBy = 'none',
    groupBy: grouper,
    manualGroups,
    getId,
    manualGroupsEnabled,
  } = options

  // Build the effective option list. The "Manuelle Gruppen" entry shows
  // up when:
  //   - manualGroups has at least one entry (any user can switch to it),
  //     OR
  //   - the page sets manualGroupsEnabled=true (admin view — option
  //     visible even when zero groups exist so the admin can create the
  //     first one).
  const effectiveOptions = useMemo<GroupOption[]>(() => {
    const hasGroups = Array.isArray(manualGroups) && manualGroups.length > 0
    const shouldShow = hasGroups || manualGroupsEnabled === true
    if (!shouldShow) return groupOptions
    // Avoid duplicating if the caller already added it.
    if (groupOptions.some((o) => o.key === 'manual')) return groupOptions
    return [...groupOptions, { key: 'manual', label: 'Manuelle Gruppen' }]
  }, [groupOptions, manualGroups, manualGroupsEnabled])

  // Persist the selected groupBy + collapsed-set independently. The grouping
  // mode lives under `dt-group:<page>` so it survives reloads. Collapsed-by-
  // group is keyed off `groups:<page>:<mode>` so switching modes doesn't
  // accidentally restore stale "Programmierung is closed" state from a
  // different axis. Storage keys are derived inline (template literals)
  // rather than via a helper closure so React's deps linter sees a stable
  // primitive instead of a recreated function reference.

  // biome-ignore lint/correctness/useExhaustiveDependencies: defaults captured on mount on purpose
  const initialMode = useMemo(() => {
    if (typeof window === 'undefined') return defaultGroupBy
    try {
      const raw = window.localStorage.getItem(`dt-group:${pageKey}`)
      if (!raw) return defaultGroupBy
      // Be defensive — the user could have changed the available modes.
      // Accept "manual" lazily here: if persisted as manual we keep it,
      // and the option will (re-)appear once `manualGroups` loads.
      if (raw === 'manual') return raw
      if (groupOptions.some((o) => o.key === raw)) return raw
      return defaultGroupBy
    } catch {
      return defaultGroupBy
    }
  }, [pageKey])

  const [groupByKey, setGroupByKey] = useState<string>(initialMode)
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    loadCollapsed(`groups:${pageKey}:${initialMode}`),
  )

  // Persist mode on change. Empty try/catch guards quota errors in private mode.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(`dt-group:${pageKey}`, groupByKey)
    } catch {}
  }, [pageKey, groupByKey])

  // Persist collapsed-set on change. Storage key depends on the mode so
  // switching modes also reloads the right set (handled in setGroupBy below).
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const arr = Array.from(collapsed)
      window.localStorage.setItem(`groups:${pageKey}:${groupByKey}`, JSON.stringify(arr))
    } catch {}
  }, [pageKey, collapsed, groupByKey])

  const setGroupBy = useCallback(
    (key: string) => {
      // Swap mode + rehydrate collapse-set for the new axis in one tick.
      setGroupByKey(key)
      setCollapsed(loadCollapsed(`groups:${pageKey}:${key}`))
    },
    [pageKey],
  )

  const disabled = groupByKey === 'none'

  // Bin the current page's rows. Stable order: first appearance wins. We
  // deliberately group AFTER pagination so the page-size knob still
  // controls how many rows appear, regardless of grouping.
  const groups = useMemo<Group<T>[]>(() => {
    if (disabled) return []

    // ── Manual-group mode ──────────────────────────────────────────
    // Pre-built buckets keyed by group id, plus a synthetic "Nicht
    // gruppiert" bucket for rows that aren't in any group.
    if (groupByKey === 'manual') {
      if (!getId) return []
      const groupList = manualGroups ?? []
      const memberToGroup = new Map<string, string>()
      for (const g of groupList) {
        for (const mid of g.memberIds) {
          // First wins — a member listed in multiple groups still maps
          // to a single bucket (matches the backend's UNIQUE constraint
          // expectation per (groupId, resourceId), not a hard cross-
          // group uniqueness).
          if (!memberToGroup.has(mid)) memberToGroup.set(mid, g.id)
        }
      }
      const buckets = new Map<string, Group<T>>()
      // Pre-seed buckets so empty groups still render in 'manage' axis
      // — but only if they have at least one item on the current page;
      // empty buckets are filtered out below.
      for (const g of groupList) {
        buckets.set(g.id, {
          key: g.id,
          label: g.name,
          items: [],
          collapsed: collapsed.has(g.id),
          restrictedCount: 0,
          color: g.color,
        })
      }
      const UNGROUPED_KEY = '__ungrouped__'
      buckets.set(UNGROUPED_KEY, {
        key: UNGROUPED_KEY,
        label: 'Nicht gruppiert',
        items: [],
        collapsed: collapsed.has(UNGROUPED_KEY),
        restrictedCount: 0,
        muted: true,
      })
      for (const row of table.rows) {
        const id = getId(row)
        const gid = memberToGroup.get(id) ?? UNGROUPED_KEY
        const bucket = buckets.get(gid) ?? buckets.get(UNGROUPED_KEY)
        if (!bucket) continue
        bucket.items.push(row)
        if (row.restricted) bucket.restrictedCount += 1
      }
      // Drop empty buckets — keep order: groups by sortOrder/name (as
      // delivered), then the synthetic ungrouped one last.
      const out: Group<T>[] = []
      for (const g of groupList) {
        const bucket = buckets.get(g.id)
        if (bucket && bucket.items.length > 0) out.push(bucket)
      }
      const ung = buckets.get(UNGROUPED_KEY)
      if (ung && ung.items.length > 0) out.push(ung)
      return out
    }

    const map = new Map<string, Group<T>>()
    for (const row of table.rows) {
      const g = grouper(row, groupByKey)
      const key = g?.key ?? '__none__'
      const label = g?.label ?? 'Ohne Gruppe'
      let bucket = map.get(key)
      if (!bucket) {
        bucket = {
          key,
          label,
          items: [],
          collapsed: collapsed.has(key),
          restrictedCount: 0,
        }
        map.set(key, bucket)
      }
      bucket.items.push(row)
      if (row.restricted) bucket.restrictedCount += 1
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'de'))
  }, [disabled, table.rows, grouper, groupByKey, collapsed, manualGroups, getId])

  const visibleRows = useMemo<T[]>(() => {
    if (disabled) return table.rows
    const out: T[] = []
    for (const g of groups) {
      if (!g.collapsed) out.push(...g.items)
    }
    return out
  }, [disabled, groups, table.rows])

  const allGroupKeys = useMemo(() => groups.map((g) => g.key), [groups])

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const expandAll = useCallback(() => {
    setCollapsed(new Set())
  }, [])

  const collapseAll = useCallback(() => {
    setCollapsed(new Set(allGroupKeys))
  }, [allGroupKeys])

  const allCollapsed = !disabled && groups.length > 0 && groups.every((g) => g.collapsed)

  return {
    groupBy: groupByKey,
    setGroupBy,
    options: effectiveOptions,
    disabled,
    groups,
    visibleRows,
    allGroupKeys,
    toggleGroup,
    expandAll,
    collapseAll,
    allCollapsed,
  }
}

function loadCollapsed(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return new Set()
    return new Set(arr.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

// ── Helpers shared by the four Stammdaten pages ────────────────────────

/** First-letter group label like „A", „B"…, fallback „#" for empties. */
export function firstLetterGroup(value: string | null | undefined): {
  key: string
  label: string
} {
  const v = (value ?? '').trim()
  if (!v) return { key: '#', label: '#' }
  const ch = v[0].toLocaleUpperCase('de')
  if (/[A-ZÄÖÜ]/.test(ch)) return { key: ch, label: ch }
  return { key: '#', label: '#' }
}
